import type { BucketAggregate } from "../../../schemas/time-series/data-stream-query.js";
import { dayMs, hourMs } from "../../../timeConstants.js";
import { UnitCategory } from "../../SensorConfigurations/SensorConfigurationTypes.js";
import { Unit } from "../UnitConfigurations.js";

export type UnitCategoryConfiguration = {
    allowedUnits: Unit[];
    databaseUnit: Unit;
    /** A stream silent longer than this is drawn as a gap instead of a line. */
    staleAfterMs: number;

    /**
     * The bucket aggregate that is *meaningful* for series in this category, where that is something
     * other than a plain mean. Advisory: the data-point API still honours whatever a caller asks for,
     * and omitting this reads as "mean", which is right for the instantaneous spot readings that make
     * up most of the catalogue.
     *
     * It belongs to the category because it follows from the phenomenon being measured, not from the
     * device that reported it:
     *
     * - An **accumulation** over the reporting interval (`precipitation`, `evapotranspiration`) wants
     *   `sum`, which is exact and, being invariant to how the period was partitioned, survives a
     *   station changing its reporting interval mid-series.
     * - An **interval extremum** (`windGust`) wants `max`, which is likewise exact: the maximum of a
     *   set of interval maxima is the maximum of the underlying samples. Note this makes the
     *   peak-value question exact and nothing more — distributional queries over such a series
     *   (median, percentile, threshold counts) still scale with the reporting interval.
     * - A **running total or a categorical state** (`electricalPulseCount`, `outputOperatingMode`,
     *   `solenoidVerification`) wants `last`, the value as of the end of the bucket. A cumulative
     *   counter's mean is its total at no instant anyone asked about, and an enum's mean is not a
     *   member of the enum. `last` beats `max` for the counter because it reports a device reset
     *   honestly instead of hiding it behind the pre-reset peak.
     *
     * A phenomenon that aggregates unlike its neighbours therefore earns its own category rather than
     * an override further down: `windGust` is split from `windSpeed` for exactly this reason, even
     * though the two share units, a conversion, and an instrument.
     *
     * `dimensionless` is the converse, and stays unset: it holds lifetime counters, opaque codes, and
     * 0/1 state together, and the `state` kind is *meant* to be averaged — that is how a window yields
     * the fraction of time an output was active. No one aggregate suits the category as it stands, so
     * expressing a preference for the counters there would mean splitting them out first.
     */
    preferredBucketAggregate?: BucketAggregate;
};

export const UnitCategoryConfigurations: Record<UnitCategory, UnitCategoryConfiguration> = {
    liquidFlow: {
        staleAfterMs: hourMs * 12,
        databaseUnit: "liters_per_minute",
        allowedUnits: ["liters_per_minute", "liters_per_hour", "gallons_per_minute", "gallons_per_hour"],
    },
    humidity: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "percent_rh",
        allowedUnits: ["percent_rh"],
    },
    airTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    boardTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    plantTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    soilTemperature: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "celsius",
        allowedUnits: ["celsius", "fahrenheit"],
    },
    liquidPressure: {
        staleAfterMs: dayMs * 2,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "psi", "bar"],
    },
    volumetricSoilMoisture: {
        staleAfterMs: hourMs * 12,
        databaseUnit: "percent",
        allowedUnits: ["percent"],
    },
    resistiveSoilMoisture: {
        staleAfterMs: hourMs * 12,
        allowedUnits: ["kilopascal"],
        databaseUnit: "kilopascal",
    },
    soilTension: {
        staleAfterMs: hourMs * 12,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal"],
    },
    soilElectricalConductivity: {
        staleAfterMs: dayMs,
        databaseUnit: "microsiemens_per_cm",
        allowedUnits: ["microsiemens_per_cm", "deciSiemens_per_meter"],
    },
    windSpeed: {
        staleAfterMs: dayMs,
        databaseUnit: "meters_per_second",
        allowedUnits: ["meters_per_second", "kilometer_per_hour", "miles_per_hour"],
    },
    windGust: {
        staleAfterMs: dayMs,
        databaseUnit: "meters_per_second",
        allowedUnits: ["meters_per_second", "kilometer_per_hour", "miles_per_hour"],
        preferredBucketAggregate: "max",
    },
    windHeading: {
        staleAfterMs: dayMs,
        databaseUnit: "degree",
        allowedUnits: ["degree"],
    },
    soilVolumetricIonContent: {
        staleAfterMs: dayMs,
        databaseUnit: "percent_vic",
        allowedUnits: ["percent_vic"],
    },
    precipitation: {
        staleAfterMs: dayMs,
        databaseUnit: "millimeter",
        allowedUnits: ["millimeter", "inch"],
        preferredBucketAggregate: "sum",
    },
    liquidTankLevel: {
        staleAfterMs: dayMs,
        // Every liquidTankLevel sensor converts volts to metres of head above the sensor, not a
        // percent-full-of-vessel figure — that would need tank geometry we don't model anywhere yet.
        databaseUnit: "meter",
        allowedUnits: ["meter", "foot"],
    },
    sapFlow: {
        staleAfterMs: dayMs,
        databaseUnit: "liters_per_hour",
        allowedUnits: ["liters_per_hour", "gallons_per_hour"],
    },
    stemWaterPotential: {
        staleAfterMs: dayMs,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "psi", "bar"],
    },
    electricalVoltage: {
        staleAfterMs: dayMs,
        databaseUnit: "volt",
        allowedUnits: ["volt"],
    },
    evapotranspiration: {
        staleAfterMs: dayMs,
        databaseUnit: "millimeter",
        allowedUnits: ["millimeter", "inch"],
        preferredBucketAggregate: "sum",
    },
    electricalPulseCount: {
        staleAfterMs: dayMs,
        databaseUnit: "pulse",
        allowedUnits: ["pulse"],
        preferredBucketAggregate: "last",
    },
    flowmeterKFactor: {
        staleAfterMs: dayMs,
        databaseUnit: "liters_per_pulse",
        allowedUnits: ["liters_per_pulse", "gallons_per_pulse"],
    },
    flowmeterOffset: {
        staleAfterMs: dayMs,
        databaseUnit: "hertz",
        allowedUnits: ["pulse_per_minute"],
    },
    flowmeterMinimumFrequency: {
        staleAfterMs: dayMs,
        databaseUnit: "hertz",
        allowedUnits: ["pulse_per_minute"],
    },
    pressureTransducerSlope: {
        staleAfterMs: dayMs,
        databaseUnit: "kilopascal_per_volt",
        allowedUnits: ["kilopascal_per_volt"],
    },
    liquidTankLevelSlope: {
        staleAfterMs: dayMs,
        databaseUnit: "meter_per_volt",
        allowedUnits: ["meter_per_volt"],
    },
    dimensionless: {
        staleAfterMs: dayMs,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
    },
    arbitraryPercentage: {
        staleAfterMs: dayMs,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
    },
    /** Categorical; see: {@link import("../../SensorConfigurations/sensorElements/outputPositionSensorElement.js").outputPositionElementConfig}. */
    outputPosition: {
        staleAfterMs: dayMs * 7,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
    },
    /** Categorical; see: {@link import("../../SensorConfigurations/outputOperatingMode.js").OutputOperatingMode}. */
    outputOperatingMode: {
        staleAfterMs: dayMs * 7,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        preferredBucketAggregate: "last",
    },
    /** Categorical; see: {@link import("../../SensorConfigurations/solenoidVerification.js").SolenoidVerificationResult}. */
    solenoidVerification: {
        staleAfterMs: dayMs * 7,
        databaseUnit: "dimensionless",
        allowedUnits: ["dimensionless"],
        preferredBucketAggregate: "last",
    },
    ndvi: {
        staleAfterMs: dayMs,
        databaseUnit: "ndvi",
        allowedUnits: ["ndvi"],
    },
    ndviVariance: {
        staleAfterMs: dayMs,
        databaseUnit: "ndvi",
        allowedUnits: ["ndvi"],
    },
    stemRadiusChange: {
        staleAfterMs: dayMs,
        databaseUnit: "micrometer",
        allowedUnits: ["micrometer"],
    },
    solarRadiation: {
        staleAfterMs: dayMs,
        databaseUnit: "watt_per_square_meter",
        allowedUnits: ["watt_per_square_meter"],
    },
    depth: {
        staleAfterMs: dayMs,
        databaseUnit: "meter",
        allowedUnits: ["meter", "centimeter", "foot", "inch"],
    },
    barometricPressure: {
        staleAfterMs: dayMs,
        databaseUnit: "kilopascal",
        allowedUnits: ["kilopascal", "hectopascal", "inches_of_mercury"],
    },
    uvIndex: {
        staleAfterMs: dayMs,
        databaseUnit: "uv_index",
        allowedUnits: ["uv_index"],
    },
} as const satisfies Record<UnitCategory, UnitCategoryConfiguration>;

/**
 * Runtime type guard for `unitCategory` values arriving over the wire (typed loosely as `string`).
 * The keys of `UnitCategoryConfigurations` are the authoritative known set. Uses `hasOwnProperty`
 * so inherited prototype keys (e.g. "toString") are not mistaken for known categories.
 */
export function isKnownUnitCategory(value: string): value is UnitCategory {
    return Object.prototype.hasOwnProperty.call(UnitCategoryConfigurations, value);
}

/** Safe accessor: returns the configuration for a known category, or `undefined` for an unknown one. */
export function getUnitCategoryConfiguration(value: string): UnitCategoryConfiguration | undefined {
    return isKnownUnitCategory(value) ? UnitCategoryConfigurations[value] : undefined;
}
